import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { T, NUMERIC } from './orders.theme';
import { COPY } from './orders.copy';
import { ChevronLeftIcon, ChevronRightIcon } from './OrdersIcons';

const FMT = 'YYYY-MM-DD';
/** Monday-first, matching the mobile app's calendar. */
const FIRST_DAY_OF_WEEK = 1;

/**
 * Presets only SET the selection — Apply still confirms it, so a preset and a
 * hand-picked range behave identically.
 */
export const PRESETS = [
  {
    id: 'today',
    label: COPY.today,
    resolve: () => ({ start: moment(), end: moment() }),
  },
  {
    id: 'yesterday',
    label: COPY.yesterday,
    resolve: () => ({
      start: moment().subtract(1, 'day'),
      end: moment().subtract(1, 'day'),
    }),
  },
  {
    id: 'last7',
    label: COPY.last7,
    resolve: () => ({ start: moment().subtract(6, 'days'), end: moment() }),
  },
  {
    id: 'thisMonth',
    label: COPY.thisMonth,
    resolve: () => ({ start: moment().startOf('month'), end: moment() }),
  },
];

/** Six rows of seven, so the grid never changes height month to month. */
function monthGrid(cursor) {
  const first = moment(cursor).startOf('month');
  const offset = (first.day() - FIRST_DAY_OF_WEEK + 7) % 7;
  const gridStart = moment(first).subtract(offset, 'days');

  return Array.from({ length: 42 }, (_, i) => moment(gridStart).add(i, 'days'));
}

const WEEKDAYS = Array.from({ length: 7 }, (_, i) =>
  moment().day((FIRST_DAY_OF_WEEK + i) % 7).format('dd')[0]
);

export default function DateRangePopover({ range, onApply, onClose, anchorRef }) {
  const [start, setStart] = useState(() => moment(range.start, FMT));
  const [end, setEnd] = useState(() => moment(range.end, FMT));
  /** True once a start is picked and we are waiting for the end of the range. */
  const [picking, setPicking] = useState(false);
  const [cursor, setCursor] = useState(() => moment(range.end, FMT).startOf('month'));

  const popoverRef = useRef(null);

  // Dismiss on outside click or Escape. The anchor is excluded so clicking the
  // date button again closes rather than immediately reopening.
  useEffect(() => {
    const onPointerDown = (event) => {
      const inPopover = popoverRef.current && popoverRef.current.contains(event.target);
      const inAnchor = anchorRef?.current && anchorRef.current.contains(event.target);
      if (!inPopover && !inAnchor) onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose, anchorRef]);

  const grid = useMemo(() => monthGrid(cursor), [cursor]);

  const activePreset = useMemo(
    () =>
      PRESETS.find((p) => {
        const r = p.resolve();
        return r.start.isSame(start, 'day') && r.end.isSame(end, 'day');
      }),
    [start, end]
  );

  const applyPreset = (preset) => {
    const r = preset.resolve();
    setStart(r.start);
    setEnd(r.end);
    setPicking(false);
    setCursor(moment(r.end).startOf('month'));
  };

  const pickDay = (day) => {
    if (!picking) {
      setStart(day);
      setEnd(day);
      setPicking(true);
      return;
    }
    // Second click closes the range, in whichever direction it was drawn.
    if (day.isBefore(start, 'day')) {
      setEnd(moment(start));
      setStart(day);
    } else {
      setEnd(day);
    }
    setPicking(false);
  };

  const dayStyle = (day) => {
    const inMonth = day.isSame(cursor, 'month');
    const isStart = day.isSame(start, 'day');
    const isEnd = day.isSame(end, 'day');
    const inRange = day.isBetween(start, end, 'day', '[]');
    const isFuture = day.isAfter(moment(), 'day');

    const base = {
      height: 30,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      border: 'none',
      background: 'transparent',
      cursor: isFuture ? 'not-allowed' : 'pointer',
      font: 'inherit',
      padding: 0,
      ...NUMERIC,
    };

    if (isStart || isEnd) {
      return {
        ...base,
        background: T.brand,
        color: T.surface,
        fontWeight: 700,
        borderRadius:
          isStart && isEnd ? T.radiusSmall : isStart ? '7px 0 0 7px' : '0 7px 7px 0',
      };
    }
    if (inRange) {
      return { ...base, background: T.selectedBg, color: T.brandInk, fontWeight: 600 };
    }
    return {
      ...base,
      color: isFuture || !inMonth ? T.inkGhost : T.ink,
      borderRadius: T.radiusSmall,
    };
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={COPY.date}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: 0,
        zIndex: 40,
        width: 318,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        boxShadow: T.popover,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {PRESETS.map((preset) => {
          const active = activePreset?.id === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset)}
              style={{
                height: 31,
                borderRadius: T.radiusSmall,
                background: active ? T.selectedBg : T.surface,
                border: `1px solid ${active ? T.selectedBorder : T.border}`,
                color: active ? T.brandInk : T.inkSoft,
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div style={{ height: 1, background: T.hairline }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px' }}>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setCursor(moment(cursor).subtract(1, 'month'))}
          style={iconButton}
        >
          <ChevronLeftIcon size={15} color={T.inkFaint} width={1.8} />
        </button>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
          {cursor.format('MMMM YYYY')}
        </span>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setCursor(moment(cursor).add(1, 'month'))}
          style={iconButton}
        >
          <ChevronRightIcon size={15} color={T.inkFaint} width={1.8} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 2 }}>
        {WEEKDAYS.map((d, i) => (
          <span
            key={i}
            style={{ textAlign: 'center', fontSize: 11.5, color: T.inkFaint }}
          >
            {d}
          </span>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gap: 2 }}>
        {grid.map((day) => (
          <button
            key={day.format(FMT)}
            type="button"
            disabled={day.isAfter(moment(), 'day')}
            onClick={() => pickDay(day)}
            style={dayStyle(day)}
          >
            {day.date()}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onApply({ start: start.format(FMT), end: end.format(FMT) })}
        style={{
          height: 36,
          borderRadius: T.radiusControl,
          background: T.brand,
          border: 'none',
          color: T.surface,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        {COPY.apply}
      </button>
    </div>
  );
}

const iconButton = {
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
};
